import React, { Suspense, useEffect, useState, lazy, memo } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Grid, Stack, CircularProgress } from '@mui/material';
import axios from 'api/request';
import { endPoints } from 'api/endpoints';
import { useMutation } from 'react-query';
import { useRecoilState } from 'recoil';
import toast from 'react-hot-toast';
import Empty from 'components/empty';
import { useParams, useLocation } from 'react-router-dom';
import { catalogs } from 'store';
import {
    OrderPayload,
    validOnboardingForm,
    toggleModal,
    showPreviewOrder,
    isSonarQubeRepoAvailable,
    costEstimateState,
    urlParams,
} from 'pages/project-onboarding/store';
import {
    resetOrder,
    resetValidOnboardingForm,
    resetToggleModal,
    formOrderObjFromOrderDetails,
    formToolsOrderObjFromOrderDetails,
} from 'pages/project-onboarding/utils';
import ProjectOnboardingStyles from 'pages/project-onboarding/style';
import ConfigureServiceModal from 'pages/project-onboarding/components/Form/ConfigureServiceModal';
import PreviewOrderDetails from 'pages/project-onboarding/components/preview/preview-order-details';
import { PRE_PREVIEW_ORDER } from 'pages/project-onboarding/utils/constants';
import { ProjectOnboardCatalogDefinitionId } from 'utils/catalogDefinitionId';
// import LoadFeatureData from 'components/load-feature-data/LoadFeatureData';
// import featureData from 'pages/project-onboarding/utils/FeatureData';

const StickyHeader = lazy(() => import('pages/project-onboarding/components/sticky-header'));
const InputForm = lazy(() => import('pages/project-onboarding/InputForm'));

const ProjectOnboarding = () => {
    const { catalogCode, cartItemId, operation } = useParams();
    const [day2Catalogs] = useRecoilState(catalogs);
    const [, setParams] = useRecoilState(urlParams);
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [showPreview, setShowPreview] = useRecoilState(showPreviewOrder);
    const [, setCostEstimateDetails] = useRecoilState(costEstimateState);
    const [validProvisionForm, setValidProvisionForm] = useRecoilState(validOnboardingForm);
    const [, setToggleModal] = useRecoilState(toggleModal);
    const [isLoading, setLoading] = useState(false);
    const [, setAvailableRepo] = useRecoilState(isSonarQubeRepoAvailable);

    const location = useLocation();
    const queryParameters = new URLSearchParams(location.search);
    const journeyId = queryParameters.get('journeyId');
    const appRegistrationId = queryParameters.get('applicationId');

    const catalogData = day2Catalogs?.find((item) => ProjectOnboardCatalogDefinitionId?.includes(item?.catalogDefinitionId));
    const orderDetailsApi = useMutation(() => axios.get(`${endPoints?.day2Operation?.ProjectOnboarding.Jwt.orderDetails}/${cartItemId}`));
    const learningDetailsApi = useMutation(() => axios.get(endPoints?.day2Operation?.ProjectOnboarding.learningApi));
    const costApi = useMutation((payload) => axios.post(endPoints?.day2Operation?.ProjectOnboarding?.costApi, payload));
    const prepopulateToolsApi = useMutation(() =>
        axios.get(`${endPoints?.devX?.prepolulateTools?.replace('{journeyId}', journeyId)}?appRegistrationId=${appRegistrationId}`)
    );
    const validateServiceAccount = useMutation((data) =>
        axios.get(`${endPoints.day2Operation.ProjectOnboarding?.Jwt?.validateServiceAccount}?serviceAccountId=${data?.serviceAccount}`)
    );

    useEffect(() => {
        toast.dismiss();
    }, [order]);

    const handleReset = () => {
        setOrder(resetOrder);
        setValidProvisionForm(resetValidOnboardingForm);
        setToggleModal(resetToggleModal);
        setShowPreview(null);
        setAvailableRepo(true);
        setParams({ journeyId: null, appRegistrationId: null });
    };

    async function checkCatalog() {
        setLoading(true);

        try {
            if (operation === 'edit') {
                const { data: orderDetails } = await orderDetailsApi.mutateAsync();
                const orderObj = formOrderObjFromOrderDetails(orderDetails);
                // eslint-disable-next-line
                setOrder((previous) => ({
                    ...previous,
                    ...orderObj,
                }));
                if (orderObj?.clarityId) setValidProvisionForm({ ...validProvisionForm, isClarityIdValid: true });
            } else {
                setOrder((previous) => ({
                    ...previous,
                    catalogProvider: catalogData?.cloudProvider,
                    catalogType: catalogData?.catalogType,
                    cloudService: catalogData?.cloudService,
                    isTouched: false,
                }));
            }
            setLoading(false);
        } catch (err) {
            setLoading(false);
        }
    }

    const isShowBackBtn = () => {
        if (showPreview === PRE_PREVIEW_ORDER) {
            return true;
        }
        return false;
    };
    const handleBackBtn = () => {
        if (showPreview === PRE_PREVIEW_ORDER) {
            setShowPreview(null);
        }
    };
    const getOrderDetails = async () => {
        try {
            if (cartItemId && operation === 'edit') {
                const { data: orderDetails } = await orderDetailsApi.mutateAsync();
                const orderObj = formOrderObjFromOrderDetails(orderDetails);
                // eslint-disable-next-line
                setOrder((previous) => ({
                    ...previous,
                    ...orderObj,
                }));
            }
        } catch (error) {
            // eslint-disable-next-line no-empty
        }
    };

    const getToolsOrderDetails = async () => {
        try {
            if (journeyId && appRegistrationId) {
                const orderDetails = await prepopulateToolsApi.mutateAsync();
                const orderObj = formToolsOrderObjFromOrderDetails(orderDetails);
                // eslint-disable-next-line
                setOrder((previous) => ({
                    ...previous,
                    ...orderObj,
                }));

                if (orderObj?.serviceAccount && orderObj?.serviceAccount?.trim()?.length > 3) {
                    validateServiceAccount.mutate(orderObj);
                }
            }
        } catch (error) {
            // eslint-disable-next-line no-empty
        }
    };

    useEffect(() => {
        if (learningDetailsApi?.isSuccess && learningDetailsApi?.data?.data) {
            setOrder({
                ...order,
                courseDetails: learningDetailsApi?.data?.data?.myLearningCoursesList,
            });
        }
    }, [learningDetailsApi?.isSuccess]);

    useEffect(() => {
        if (catalogCode) {
            handleReset();
            checkCatalog();
            learningDetailsApi?.mutate();
        }
    }, [catalogCode]);
    useEffect(() => {
        if (catalogCode) {
            handleReset();
            checkCatalog();
        }
    }, [operation]);

    /* eslint-disable arrow-body-style */
    useEffect(() => {
        return () => handleReset();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        handleReset();
        getOrderDetails();
    }, [operation, cartItemId]);

    const getCostDetail = () => {
        const costPayload = {
            catalogServiceCode: catalogCode,
            sector: order?.sector,
            quantity: 1,
            catalogConfig: {
                testPlans: {
                    hCode: order?.hCode,
                    members: order?.testPlanUserList,
                },
            },
        };

        costApi.mutate(costPayload);
    };

    useEffect(() => {
        if (order?.testPlanUserList && order?.testPlanUserList?.length > 0) getCostDetail();
    }, [order?.testPlanUserList]);

    useEffect(() => {
        if (costApi?.isLoading) {
            setOrder({
                ...order,
                isCostApiLoading: true,
            });
        }
    }, [costApi?.isLoading]);

    useEffect(() => {
        if (costApi?.data?.data) {
            setOrder({ ...order, estimatedCost: costApi?.data?.data, isCostApiLoading: false });
            setCostEstimateDetails(costApi?.data?.data);
        }
    }, [costApi?.isSuccess]);

    React.useEffect(() => {
        if (validateServiceAccount?.isSuccess && validateServiceAccount?.data?.data) {
            setValidProvisionForm({ ...validProvisionForm, isInvalidServiceAccount: false });
        }
        if (validateServiceAccount?.isSuccess && !validateServiceAccount?.data?.data) {
            setValidProvisionForm({ ...validProvisionForm, isInvalidServiceAccount: true });
        }
    }, [validateServiceAccount?.isSuccess]);

    React.useEffect(() => {
        if (journeyId && appRegistrationId) {
            setParams({
                journeyId,
                appRegistrationId,
            });
            getToolsOrderDetails();
        }
    }, [journeyId, appRegistrationId]);

    return (
        <>
            <Helmet>
                <title>DevOps Project Onboarding</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProjectOnboardingStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProjectOnboardingStyles.circularProgressColor} />
                    </Box>
                }
            >
                {isLoading && (
                    <Stack sx={ProjectOnboardingStyles?.emptyStack}>
                        <Empty isLoading color="#000000" msg="Loading..." />
                    </Stack>
                )}
                {!isLoading && (
                    <>
                        <StickyHeader title="Azure DevOps Project Onboarding" imgSrc={null} back={isShowBackBtn()} handleBackBtn={() => handleBackBtn()} />
                        <Grid item container spacing={2}>
                            <Grid item xs={12}>
                                <Grid display="flex" flexDirection="column" paddingY={4} gap={3} data-testId="project-onboard" className="contentContainer">
                                    {/* <LoadFeatureData order={order} setOrder={setOrder} featureData={featureData} /> */}
                                    {showPreview === PRE_PREVIEW_ORDER && <PreviewOrderDetails />}
                                    {!showPreview && (
                                        <>
                                            <InputForm />
                                            <ConfigureServiceModal />
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Suspense>
        </>
    );
};

export default memo(ProjectOnboarding);
